import yjsDocumentService from '../../../infrastructure/sync/YjsDocumentService';
import {isWorkspaceReadOnly} from '../../../shared/services/workspaceAccess';

export const FAVORITES_MAP = 'songFavorites:v1';
export const USAGE_MAP = 'songUsageDaily:v1';
export const LIBRARY_MANIFEST = '.chwala-song-library.json';
export const LIBRARY_LOCAL_EVENT = 'chwala-song-library-change';
const localKey = id => `chwala-song-library:v1:${id}`;
const empty = () => ({version: 1, favorites: [], usage: []});
const validKey = value => typeof value === 'string' && /^song:[a-f0-9]{64}$/.test(value);
export const validateLibraryState = state => {
  if (!state || state.version !== 1 || !Array.isArray(state.favorites) || !Array.isArray(state.usage)) {
    throw new Error('Nieprawidłowe dane ulubionych i historii w kopii.');
  }
  const validBase = entry => entry && validKey(entry.songKey) && typeof entry.title === 'string';
  if (!state.favorites.every(validBase) || !state.usage.every(entry => validBase(entry) &&
    Number.isSafeInteger(entry.count) && entry.count > 0 && typeof entry.id === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(entry.day) && Number.isFinite(Date.parse(entry.lastAt)) &&
    typeof entry.source?.fileName === 'string' && Number.isInteger(entry.source.songNumber) && entry.source.songNumber > 0)) {
    throw new Error('Nieprawidłowe wpisy ulubionych lub historii w kopii.');
  }
  return state;
};

export const readSongLibraryState = workspaceId => {
  if (process.env.REACT_APP_HOSANNA_DIRECT === '1' || isWorkspaceReadOnly(workspaceId)) {
    const raw = localStorage.getItem(localKey(workspaceId));
    if (!raw) return empty();
    try { return validateLibraryState(JSON.parse(raw)); } catch { return empty(); }
  }
  const {doc} = yjsDocumentService.connectToDocument(workspaceId);
  return {version: 1, favorites: [...doc.getMap(FAVORITES_MAP).values()].sort((a, b) => a.songKey.localeCompare(b.songKey)), usage: [...doc.getMap(USAGE_MAP).values()].sort((a, b) => a.id.localeCompare(b.id))};
};

const mutate = (workspaceId, operation) => {
  if (process.env.REACT_APP_HOSANNA_DIRECT === '1' || isWorkspaceReadOnly(workspaceId)) {
    const state = readSongLibraryState(workspaceId);
    const favorites = new Map(state.favorites.map(entry => [entry.songKey, entry]));
    const usage = new Map(state.usage.map(entry => [entry.id, entry]));
    operation(favorites, usage, 'local');
    localStorage.setItem(localKey(workspaceId), JSON.stringify({version: 1, favorites: [...favorites.values()], usage: [...usage.values()]}));
    window.dispatchEvent(new Event(LIBRARY_LOCAL_EVENT));
  } else {
    const {doc} = yjsDocumentService.connectToDocument(workspaceId);
    doc.transact(() => operation(doc.getMap(FAVORITES_MAP), doc.getMap(USAGE_MAP), String(doc.clientID)));
  }
};

export const setSongFavorite = (workspaceId, entry, enabled) => mutate(workspaceId, favorites => {
  entry.keys.forEach(songKey => {
    if (enabled) favorites.set(songKey, {songKey, title: entry.title});
    else favorites.delete(songKey);
  });
});

export const recordSongUse = (workspaceId, document, now = new Date()) => mutate(workspaceId, (_, usage, writer) => {
  const pad = value => String(value).padStart(2, '0');
  const day = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  // Each Yjs client writes its own daily counter: concurrent clients cannot overwrite counts.
  const id = `${writer}:${day}:${document.songKey}`;
  const previous = usage.get(id);
  usage.set(id, {id, day, songKey: document.songKey, title: document.title,
    count: (previous?.count || 0) + 1, lastAt: now.toISOString(),
    source: {fileName: document.fileName, songNumber: document.songNumber}});
});

export const restoreSongLibraryState = (workspaceId, state) => {
  validateLibraryState(state);
  mutate(workspaceId, (favorites, usage) => {
    favorites.clear(); usage.clear();
    state.favorites.forEach(entry => favorites.set(entry.songKey, entry));
    state.usage.forEach(entry => usage.set(entry.id, entry));
  });
};
